/**
 * Fristoversikt - Komplett oversikt over alle frister
 */

import React, { useState, useEffect } from 'react';
import crmApi from '../api/crm';
import './Fristoversikt.css';

export default function Fristoversikt() {
  const [frister, setFrister] = useState([]);
  const [loading, setLoading] = useState(true);
  const [visning, setVisning] = useState('liste'); // 'liste' eller 'kalender'
  const [filter, setFilter] = useState('alle'); // 'alle', 'aktive', 'fullførte'
  const [prioritetFilter, setPrioritetFilter] = useState('alle'); // 'alle', 'kritisk', 'høy', 'middels', 'lav'
  const [sokTekst, setSokTekst] = useState('');
  const [redigerFrist, setRedigerFrist] = useState(null);
  const [nyFrist, setNyFrist] = useState(false);
  const [saker, setSaker] = useState([]);
  const [slettFrist, setSlettFrist] = useState(null);

  useEffect(() => {
    lastFrister();
    lastSaker();
  }, []);

  const lastFrister = async () => {
    try {
      setLoading(true);
      const data = await crmApi.getFrister();
      setFrister(data.frister || []);
    } catch (error) {
      console.error('Feil ved lasting av frister:', error);
      alert('Kunne ikke laste frister');
    } finally {
      setLoading(false);
    }
  };

  const lastSaker = async () => {
    try {
      const data = await crmApi.getSaker();
      setSaker(data.saker || []);
    } catch (error) {
      console.error('Feil ved lasting av saker:', error);
    }
  };

  const handleSlettFrist = async () => {
    if (!slettFrist) return;

    try {
      console.log('Sletter frist med ID:', slettFrist.id);
      const result = await crmApi.deleteFrist(slettFrist.id);
      console.log('Sletting resultat:', result);
      alert('✅ Frist slettet');
      setSlettFrist(null);
      lastFrister();
    } catch (error) {
      console.error('Feil ved sletting:', error);
      console.error('Feil detaljer:', error.message, error);
      alert(`Kunne ikke slette frist: ${error.message || 'Ukjent feil'}`);
      setSlettFrist(null);
    }
  };

  const handleLagreFrist = async (fristData) => {
    try {
      if (redigerFrist) {
        await crmApi.updateFrist(redigerFrist.id, fristData);
        alert('✅ Frist oppdatert');
      } else {
        await crmApi.createFrist(fristData.sak_id, fristData);
        alert('✅ Frist opprettet');
      }
      setRedigerFrist(null);
      setNyFrist(false);
      lastFrister();
    } catch (error) {
      console.error('Feil ved lagring:', error);
      alert('Kunne ikke lagre frist: ' + error.message);
    }
  };

  const handleMarkerFullført = async (frist) => {
    try {
      const nyStatus = frist.status === 'fullført' ? 'aktiv' : 'fullført';

      await crmApi.updateFrist(frist.id, {
        sak_id: frist.sak_id,
        beskrivelse: frist.beskrivelse,
        frist_dato: frist.frist_dato,
        prioritet: frist.prioritet || 'middels',
        notater: frist.notater || '',
        status: nyStatus
      });

      lastFrister();
    } catch (error) {
      console.error('Feil ved oppdatering:', error);
      console.error('Feil detaljer:', error.message);
      alert(`Kunne ikke oppdatere status: ${error.message || 'Ukjent feil'}`);
    }
  };

  const filtrerteFrister = frister.filter(f => {
    // Status filter
    if (filter === 'aktive' && f.status !== 'aktiv') return false;
    if (filter === 'fullførte' && f.status !== 'fullført') return false;

    // Prioritet filter
    if (prioritetFilter !== 'alle' && f.prioritet !== prioritetFilter) return false;

    // Søketekst filter
    if (sokTekst) {
      const sok = sokTekst.toLowerCase();
      const matchBeskrivelse = f.beskrivelse?.toLowerCase().includes(sok);
      const matchSaksnummer = f.saksnummer?.toLowerCase().includes(sok);
      const matchSakTittel = f.sak_tittel?.toLowerCase().includes(sok);
      const matchNotater = f.notater?.toLowerCase().includes(sok);

      if (!matchBeskrivelse && !matchSaksnummer && !matchSakTittel && !matchNotater) return false;
    }

    return true;
  });

  const sorterteFrister = [...filtrerteFrister].sort((a, b) => {
    return new Date(a.frist_dato) - new Date(b.frist_dato);
  });

  const getPrioritetFarge = (prioritet) => {
    const farger = {
      'kritisk': 'priority-kritisk',
      'høy': 'priority-hoy',
      'middels': 'priority-middels',
      'lav': 'priority-lav'
    };
    return farger[prioritet] || 'priority-middels';
  };

  const getDagerTilFrist = (fristDato) => {
    const now = new Date();
    const frist = new Date(fristDato);
    const diff = Math.ceil((frist - now) / (1000 * 60 * 60 * 24));

    if (diff < 0) return { tekst: `${Math.abs(diff)} dager siden`, klasse: 'overdue' };
    if (diff === 0) return { tekst: 'I dag', klasse: 'today' };
    if (diff === 1) return { tekst: 'I morgen', klasse: 'tomorrow' };
    if (diff <= 7) return { tekst: `Om ${diff} dager`, klasse: 'soon' };
    return { tekst: `Om ${diff} dager`, klasse: 'normal' };
  };

  const getKalenderVisning = () => {
    const grouped = {};

    sorterteFrister.forEach(frist => {
      const dato = frist.frist_dato.split('T')[0];
      if (!grouped[dato]) grouped[dato] = [];
      grouped[dato].push(frist);
    });

    return grouped;
  };

  const handleEksporterCSV = () => {
    // Generer CSV-data med aktuelle filtrerte frister
    const headers = ['Frist dato', 'Beskrivelse', 'Sak', 'Prioritet', 'Status', 'Notater'];
    const csvData = filtrerteFrister.map(frist => [
      frist.frist_dato ? new Date(frist.frist_dato).toLocaleDateString('nb-NO') : '',
      frist.beskrivelse || '',
      frist.saksnummer ? `${frist.saksnummer} - ${frist.sak_tittel || ''}` : '',
      frist.prioritet || 'middels',
      frist.status || 'aktiv',
      frist.notater || ''
    ]);

    // Kombiner headers og data
    const csvContent = [
      headers.join(';'),
      ...csvData.map(row => row.map(cell => `"${cell}"`).join(';'))
    ].join('\n');

    // Last ned som fil
    const blob = new Blob(['\uFEFF' + csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `frister_${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  if (loading) {
    return (
      <div className="fristoversikt loading">
        <div className="spinner"></div>
        <p>Laster frister...</p>
      </div>
    );
  }

  return (
    <div className="fristoversikt">
      <div className="frist-header">
        <h1>⏰ Fristoversikt</h1>
        <div style={{display: 'flex', gap: '0.75rem'}}>
          <button className="btn-secondary" onClick={handleEksporterCSV} title="Eksporter fristliste til CSV">
            📊 Eksporter CSV
          </button>
          <button className="btn-primary" onClick={() => setNyFrist(true)}>
            ➕ Ny frist
          </button>
        </div>
      </div>

      {/* Søkefelt */}
      <div style={{marginBottom: '1.5rem'}}>
        <input
          type="text"
          placeholder="🔍 Søk i frister (beskrivelse, sak, notater)..."
          value={sokTekst}
          onChange={(e) => setSokTekst(e.target.value)}
          style={{
            width: '100%',
            padding: '0.75rem',
            fontSize: '1rem',
            border: '1px solid #ddd',
            borderRadius: '8px'
          }}
        />
      </div>

      {/* Filter og visningskontroller */}
      <div className="frist-kontroller">
        <div className="filter-buttons">
          <button
            className={filter === 'alle' ? 'active' : ''}
            onClick={() => setFilter('alle')}
          >
            Alle ({frister.length})
          </button>
          <button
            className={filter === 'aktive' ? 'active' : ''}
            onClick={() => setFilter('aktive')}
          >
            Aktive ({frister.filter(f => f.status === 'aktiv').length})
          </button>
          <button
            className={filter === 'fullførte' ? 'active' : ''}
            onClick={() => setFilter('fullførte')}
          >
            Fullførte ({frister.filter(f => f.status === 'fullført').length})
          </button>
        </div>

        <div className="visning-buttons">
          <button
            className={visning === 'liste' ? 'active' : ''}
            onClick={() => setVisning('liste')}
          >
            📋 Liste
          </button>
          <button
            className={visning === 'kalender' ? 'active' : ''}
            onClick={() => setVisning('kalender')}
          >
            📅 Kalender
          </button>
        </div>
      </div>

      {/* Prioritetsfilter */}
      <div className="prioritet-filtre">
        <label style={{fontWeight: '600', fontSize: '0.9rem', color: '#666', marginRight: '1rem'}}>
          Prioritet:
        </label>
        <div className="prioritet-buttons">
          <button
            className={`prioritet-filter-btn ${prioritetFilter === 'alle' ? 'active' : ''}`}
            onClick={() => setPrioritetFilter('alle')}
          >
            Alle ({frister.length})
          </button>
          <button
            className={`prioritet-filter-btn kritisk ${prioritetFilter === 'kritisk' ? 'active' : ''}`}
            onClick={() => setPrioritetFilter('kritisk')}
          >
            🔴 Kritisk ({frister.filter(f => f.prioritet === 'kritisk').length})
          </button>
          <button
            className={`prioritet-filter-btn høy ${prioritetFilter === 'høy' ? 'active' : ''}`}
            onClick={() => setPrioritetFilter('høy')}
          >
            🟠 Høy ({frister.filter(f => f.prioritet === 'høy').length})
          </button>
          <button
            className={`prioritet-filter-btn middels ${prioritetFilter === 'middels' ? 'active' : ''}`}
            onClick={() => setPrioritetFilter('middels')}
          >
            🟡 Middels ({frister.filter(f => f.prioritet === 'middels').length})
          </button>
          <button
            className={`prioritet-filter-btn lav ${prioritetFilter === 'lav' ? 'active' : ''}`}
            onClick={() => setPrioritetFilter('lav')}
          >
            🟢 Lav ({frister.filter(f => f.prioritet === 'lav').length})
          </button>
        </div>
      </div>

      {/* Listevisning */}
      {visning === 'liste' && (
        <div className="frist-liste">
          {sorterteFrister.length === 0 ? (
            <div className="empty-state">
              <p>
                {sokTekst
                  ? 'Ingen frister matchet søket'
                  : `Ingen frister ${filter !== 'alle' ? `med status "${filter}"` : ''}`
                }
              </p>
            </div>
          ) : (
            sorterteFrister.map(frist => {
              const dagerTil = getDagerTilFrist(frist.frist_dato);

              return (
                <div key={frist.id} className={`frist-card ${frist.status}`}>
                  <div className="frist-checkbox" onClick={(e) => e.stopPropagation()}>
                    <input
                      type="checkbox"
                      checked={frist.status === 'fullført'}
                      onChange={(e) => {
                        e.stopPropagation();
                        handleMarkerFullført(frist);
                      }}
                      onClick={(e) => e.stopPropagation()}
                    />
                  </div>

                  <div className="frist-innhold">
                    <div className="frist-topp">
                      <h3>{frist.beskrivelse}</h3>
                      <span className={`prioritet-badge ${getPrioritetFarge(frist.prioritet)}`}>
                        {frist.prioritet}
                      </span>
                    </div>

                    <div className="frist-meta">
                      <span className="frist-sak">
                        📁 {frist.saksnummer} - {frist.sak_tittel || 'Ingen sak'}
                      </span>
                      <span className={`frist-dato ${dagerTil.klasse}`}>
                        📅 {new Date(frist.frist_dato).toLocaleDateString('nb-NO')} ({dagerTil.tekst})
                      </span>
                    </div>

                    {frist.notater && (
                      <p className="frist-notater">{frist.notater}</p>
                    )}
                  </div>

                  <div className="frist-actions">
                    <button
                      className="btn-edit"
                      onClick={(e) => {
                        e.stopPropagation();
                        setRedigerFrist(frist);
                      }}
                      title="Rediger"
                    >
                      ✏️
                    </button>
                    <button
                      className="btn-delete"
                      onClick={(e) => {
                        e.stopPropagation();
                        setSlettFrist(frist);
                      }}
                      title="Slett"
                    >
                      🗑️
                    </button>
                  </div>
                </div>
              );
            })
          )}
        </div>
      )}

      {/* Kalendervisning */}
      {visning === 'kalender' && (
        <div className="frist-kalender">
          {Object.entries(getKalenderVisning()).map(([dato, dagensFrister]) => {
            const dateObj = new Date(dato);
            const dagerTil = getDagerTilFrist(dato);

            return (
              <div key={dato} className="kalender-dag">
                <div className={`dag-header ${dagerTil.klasse}`}>
                  <h3>{dateObj.toLocaleDateString('nb-NO', { weekday: 'long', day: 'numeric', month: 'long' })}</h3>
                  <span className="dag-count">{dagensFrister.length} frister</span>
                </div>

                <div className="dag-frister">
                  {dagensFrister.map(frist => (
                    <div
                      key={frist.id}
                      className={`kalender-frist ${frist.status} ${getPrioritetFarge(frist.prioritet)}`}
                      onClick={() => setRedigerFrist(frist)}
                    >
                      <div className="kalender-frist-topp">
                        <input
                          type="checkbox"
                          checked={frist.status === 'fullført'}
                          onChange={(e) => {
                            e.stopPropagation();
                            handleMarkerFullført(frist);
                          }}
                          onClick={(e) => e.stopPropagation()}
                        />
                        <h4>{frist.beskrivelse}</h4>
                      </div>
                      <p className="kalender-frist-sak">{frist.saksnummer}</p>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Modal for ny/redigert frist */}
      {(nyFrist || redigerFrist) && (
        <FristModal
          frist={redigerFrist}
          saker={saker}
          onClose={() => {
            setNyFrist(false);
            setRedigerFrist(null);
          }}
          onSave={handleLagreFrist}
        />
      )}

      {/* Sletting bekreftelse */}
      {slettFrist && (
        <div className="modal-overlay" onClick={() => setSlettFrist(null)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{maxWidth: '400px'}}>
            <div className="modal-header">
              <h2>🗑️ Slett frist</h2>
              <button className="close-btn" onClick={() => setSlettFrist(null)}>×</button>
            </div>

            <div className="modal-body">
              <p>Er du sikker på at du vil slette denne fristen?</p>
              <div style={{background: '#f5f5f5', padding: '1rem', borderRadius: '8px', marginTop: '1rem'}}>
                <p style={{margin: 0}}><strong>{slettFrist.beskrivelse}</strong></p>
                <p style={{margin: '0.5rem 0 0 0', fontSize: '0.9em', color: '#666'}}>
                  📅 {new Date(slettFrist.frist_dato).toLocaleDateString('nb-NO')}
                </p>
                {slettFrist.saksnummer && (
                  <p style={{margin: '0.3rem 0 0 0', fontSize: '0.9em', color: '#666'}}>
                    📁 {slettFrist.saksnummer}
                  </p>
                )}
              </div>
              <p style={{fontSize: '0.9em', color: '#999', marginTop: '1rem'}}>
                Denne handlingen kan ikke angres.
              </p>
            </div>

            <div className="modal-actions">
              <button className="btn-secondary" onClick={() => setSlettFrist(null)}>
                Avbryt
              </button>
              <button className="btn-danger" onClick={handleSlettFrist}>
                🗑️ Slett frist
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function FristModal({ frist, saker, onClose, onSave }) {
  const [formData, setFormData] = useState({
    sak_id: frist?.sak_id || '',
    beskrivelse: frist?.beskrivelse || '',
    frist_dato: frist?.frist_dato?.split('T')[0] || '',
    prioritet: frist?.prioritet || 'middels',
    notater: frist?.notater || '',
    status: frist?.status || 'aktiv',
    paaminnelse_1dag: frist?.paaminnelse_1dag !== 0,
    paaminnelse_3dager: frist?.paaminnelse_3dager !== 0,
    paaminnelse_7dager: frist?.paaminnelse_7dager !== 0
  });

  const handleSubmit = (e) => {
    e.preventDefault();

    if (!formData.sak_id || !formData.beskrivelse || !formData.frist_dato) {
      alert('Sak, beskrivelse og frist-dato er påkrevd');
      return;
    }

    onSave(formData);
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <button className="modal-close" onClick={onClose}>×</button>

        <h2>{frist ? '✏️ Rediger frist' : '➕ Ny frist'}</h2>

        <form onSubmit={handleSubmit}>
          <div className="form-group required">
            <label>Sak *</label>
            <select
              value={formData.sak_id}
              onChange={(e) => setFormData({ ...formData, sak_id: e.target.value })}
              required
            >
              <option value="">-- Velg sak --</option>
              {saker.map(sak => (
                <option key={sak.id} value={sak.id}>
                  {sak.saksnummer} - {sak.tittel}
                </option>
              ))}
            </select>
          </div>

          <div className="form-group required">
            <label>Beskrivelse *</label>
            <input
              type="text"
              value={formData.beskrivelse}
              onChange={(e) => setFormData({ ...formData, beskrivelse: e.target.value })}
              placeholder="F.eks. Send stevning"
              required
            />
          </div>

          <div className="form-row">
            <div className="form-group required">
              <label>Frist-dato *</label>
              <input
                type="date"
                value={formData.frist_dato}
                onChange={(e) => setFormData({ ...formData, frist_dato: e.target.value })}
                required
              />
            </div>

            <div className="form-group">
              <label>Prioritet</label>
              <select
                value={formData.prioritet}
                onChange={(e) => setFormData({ ...formData, prioritet: e.target.value })}
              >
                <option value="lav">Lav</option>
                <option value="middels">Middels</option>
                <option value="høy">Høy</option>
                <option value="kritisk">Kritisk</option>
              </select>
            </div>
          </div>

          <div className="form-group">
            <label>Notater</label>
            <textarea
              value={formData.notater}
              onChange={(e) => setFormData({ ...formData, notater: e.target.value })}
              rows="3"
              placeholder="Ekstra informasjon om fristen..."
            />
          </div>

          <div className="modal-actions">
            <button type="button" className="btn-secondary" onClick={onClose}>
              Avbryt
            </button>
            <button type="submit" className="btn-primary">
              💾 Lagre
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
